import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import type {
  CanvasHistoryState,
  CanvasStageHandle,
  Dimensions,
  DocumentChangeReason,
  DrawingSettings,
  RasterSelectionUiState,
} from "./canvasTypes";
import type { ComicSelectionState, PersistedDocumentState, StudioTool } from "../comic/comicTypes";
import { DrawingEngine } from "./DrawingEngine";
import { PointerController } from "./PointerController";
import type { LayerControllerState } from "../layers/LayerController";
import type { StoredRasterLayerBlob } from "../layers/layerTypes";
import type { EncodedRasterLayer } from "../layers/RasterLayerSurface";
import type { ShapeMode } from "../shapes/shapeTypes";
import type { FillTolerance } from "../fill/fillTypes";
import type { GuideOverlayState } from "../activities/activityTypes";

interface CanvasStageProps {
  documentSize: Dimensions;
  settings: DrawingSettings;
  initialArtworkBlob?: Blob;
  initialRasterLayers?: ReadonlyArray<StoredRasterLayerBlob>;
  initialDocumentState: PersistedDocumentState;
  dialogueLayerId?: string;
  studioTool: StudioTool;
  shapeMode: ShapeMode;
  fillTolerance: FillTolerance;
  guideOverlay: GuideOverlayState | null;
  onZoomChange: (zoomPercent: number) => void;
  onDocumentChange: (reason: DocumentChangeReason) => void;
  onHistoryChange: (state: CanvasHistoryState) => void;
  onReady: () => void;
  onRestoreError: () => void;
  onComicSelectionChange: (selection: ComicSelectionState) => void;
  onRasterSelectionChange: (selection: RasterSelectionUiState) => void;
  onComicTextEditRequest: (object: NonNullable<ComicSelectionState["object"]>) => void;
  onLayerChange: (state: LayerControllerState) => void;
  onInteractionError: (message: string) => void;
  onColourPick: (colour: string) => void;
}

export const CanvasStage = forwardRef<CanvasStageHandle, CanvasStageProps>(function CanvasStage(
  {
    documentSize,
    settings,
    initialArtworkBlob,
    initialRasterLayers,
    initialDocumentState,
    dialogueLayerId,
    studioTool,
    shapeMode,
    fillTolerance,
    guideOverlay,
    onZoomChange,
    onDocumentChange,
    onHistoryChange,
    onReady,
    onRestoreError,
    onComicSelectionChange,
    onRasterSelectionChange,
    onComicTextEditRequest,
    onLayerChange,
    onInteractionError,
    onColourPick,
  },
  ref,
) {
  const documentWidth = documentSize.width;
  const documentHeight = documentSize.height;
  const shellRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<DrawingEngine | null>(null);
  const [isReady, setIsReady] = useState(false);
  const settingsRef = useRef(settings);
  const studioToolRef = useRef(studioTool);
  const shapeModeRef = useRef(shapeMode);
  const fillToleranceRef = useRef(fillTolerance);
  const zoomListenerRef = useRef(onZoomChange);
  const documentChangeRef = useRef(onDocumentChange);
  const historyListenerRef = useRef(onHistoryChange);
  const readyListenerRef = useRef(onReady);
  const restoreErrorRef = useRef(onRestoreError);
  const selectionListenerRef = useRef(onComicSelectionChange);
  const rasterSelectionListenerRef = useRef(onRasterSelectionChange);
  const textEditListenerRef = useRef(onComicTextEditRequest);
  const layerListenerRef = useRef(onLayerChange);
  const interactionErrorRef = useRef(onInteractionError);
  const colourPickRef = useRef(onColourPick);
  settingsRef.current = settings;
  studioToolRef.current = studioTool;
  shapeModeRef.current = shapeMode;
  fillToleranceRef.current = fillTolerance;
  zoomListenerRef.current = onZoomChange;
  documentChangeRef.current = onDocumentChange;
  historyListenerRef.current = onHistoryChange;
  readyListenerRef.current = onReady;
  restoreErrorRef.current = onRestoreError;
  selectionListenerRef.current = onComicSelectionChange;
  rasterSelectionListenerRef.current = onRasterSelectionChange;
  textEditListenerRef.current = onComicTextEditRequest;
  layerListenerRef.current = onLayerChange;
  interactionErrorRef.current = onInteractionError;
  colourPickRef.current = onColourPick;

  useImperativeHandle(ref, () => ({
    fitToView: () => engineRef.current?.fitToView(),
    resetView: () => engineRef.current?.resetView(),
    finishActiveStroke: () => engineRef.current?.finishActiveInteraction(),
    finishActiveInteraction: () => engineRef.current?.finishActiveInteraction(),
    interruptActiveInteraction: (reason) => engineRef.current?.interruptActiveInteraction(reason),
    cancelActiveInteraction: () => engineRef.current?.cancelPointerInteraction(),
    revokeFullToolAccess: () => engineRef.current?.revokeFullToolAccess(),
    isStrokeActive: () => engineRef.current?.hasActiveInteraction() ?? false,
    undo: () => engineRef.current?.undo() ?? false,
    redo: () => engineRef.current?.redo() ?? false,
    exportSaveArtifacts: () => {
      const engine = engineRef.current;
      return engine
        ? engine.exportSaveArtifacts()
        : Promise.reject(new Error("The drawing canvas is not ready."));
    },
    selectPanel: (panelId) => engineRef.current?.selectPanel(panelId),
    clearSelectedPanel: () => engineRef.current?.clearSelectedPanel() ?? false,
    duplicateSelectedPanel: (targetPanelId) =>
      engineRef.current?.duplicateSelectedPanel(targetPanelId) ?? false,
    deleteSelectedComicObject: () => engineRef.current?.deleteSelectedComicObject() ?? false,
    updateSelectedComicText: (text) => engineRef.current?.updateSelectedComicText(text) ?? false,
    transformSelectedComicObject: (change) =>
      engineRef.current?.transformSelectedComicObject(change) ?? false,
    applyRasterSelection: () => engineRef.current?.applyRasterSelection() ?? false,
    clearRasterSelection: () => engineRef.current?.clearRasterSelection() ?? false,
    duplicateRasterSelection: () => engineRef.current?.duplicateRasterSelection() ?? false,
    flipRasterSelection: (axis) => engineRef.current?.flipRasterSelection(axis) ?? false,
    deleteRasterSelection: () => engineRef.current?.deleteRasterSelection() ?? false,
    transformRasterSelection: (change) =>
      engineRef.current?.transformRasterSelection(change) ?? false,
    activateLayer: (layerId) => engineRef.current?.activateLayer(layerId),
    addLayer: (preset) => engineRef.current?.addLayer(preset) ?? false,
    renameLayer: (layerId, name) => engineRef.current?.renameLayer(layerId, name) ?? false,
    setLayerVisible: (layerId, visible) =>
      engineRef.current?.setLayerVisible(layerId, visible) ?? false,
    setLayerLocked: (layerId, locked) =>
      engineRef.current?.setLayerLocked(layerId, locked) ?? false,
    setLayerOpacity: (layerId, opacity) =>
      engineRef.current?.setLayerOpacity(layerId, opacity) ?? false,
    moveLayer: (layerId, direction) => engineRef.current?.moveLayer(layerId, direction) ?? false,
    deleteLayer: (layerId) => engineRef.current?.deleteLayer(layerId) ?? false,
    clearLayer: (layerId) => engineRef.current?.clearLayer(layerId) ?? false,
    mergeLayerDown: (layerId) => engineRef.current?.mergeLayerDown(layerId) ?? false,
    acknowledgeSavedLayers: (layers: ReadonlyArray<EncodedRasterLayer>) =>
      engineRef.current?.acknowledgeSavedLayers(layers),
    exportLayerPreviews: () =>
      engineRef.current?.exportLayerPreviews() ??
      Promise.reject(new Error("The drawing canvas is not ready.")),
  }));

  useEffect(() => {
    const shell = shellRef.current;
    const canvas = canvasRef.current;
    if (!shell || !canvas) return;

    let active = true;
    let pointers: PointerController | null = null;
    setIsReady(false);
    const engine = new DrawingEngine(
      canvas,
      { width: documentWidth, height: documentHeight },
      settingsRef.current,
      studioToolRef.current,
      initialDocumentState,
      dialogueLayerId,
      (zoom) => zoomListenerRef.current(zoom),
      (reason) => documentChangeRef.current(reason),
      (state) => historyListenerRef.current(state),
      (selection) => selectionListenerRef.current(selection),
      (object) => textEditListenerRef.current(object),
      undefined,
      (state) => layerListenerRef.current(state),
      (message) => interactionErrorRef.current(message),
      (selection) => rasterSelectionListenerRef.current(selection),
      (colour) => colourPickRef.current(colour),
    );
    engine.updateRasterToolOptions(shapeModeRef.current, fillToleranceRef.current);
    engineRef.current = engine;

    const resize = (): void => {
      const bounds = shell.getBoundingClientRect();
      engine.resize(bounds.width, bounds.height, window.devicePixelRatio || 1);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(shell);
    window.addEventListener("resize", resize);
    resize();

    const restore = async (): Promise<void> => {
      try {
        if (initialRasterLayers?.length) await engine.loadRasterLayers(initialRasterLayers);
        else if (initialArtworkBlob) await engine.loadArtwork(initialArtworkBlob);
        if (!active) return;
        pointers = new PointerController(canvas, engine);
        setIsReady(true);
        readyListenerRef.current();
      } catch {
        if (!active) return;
        restoreErrorRef.current();
      }
    };
    void restore();

    return () => {
      active = false;
      observer.disconnect();
      window.removeEventListener("resize", resize);
      pointers?.destroy();
      engine.destroy();
      if (engineRef.current === engine) engineRef.current = null;
    };
  }, [
    dialogueLayerId,
    documentHeight,
    documentWidth,
    initialArtworkBlob,
    initialDocumentState,
    initialRasterLayers,
  ]);

  useEffect(() => {
    engineRef.current?.updateSettings(settings);
  }, [settings]);

  useEffect(() => {
    engineRef.current?.updateStudioTool(studioTool);
  }, [studioTool]);

  useEffect(() => {
    engineRef.current?.updateRasterToolOptions(shapeMode, fillTolerance);
  }, [fillTolerance, shapeMode]);

  useEffect(() => {
    engineRef.current?.updateGuideOverlay(guideOverlay);
  }, [guideOverlay]);

  return (
    <div className="canvas-stage-shell" ref={shellRef}>
      <canvas
        aria-label="Drawing canvas"
        className="drawing-canvas"
        data-testid="drawing-canvas"
        ref={canvasRef}
        tabIndex={0}
      />
      {!isReady ? (
        <div className="canvas-stage-loading" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          Restoring your drawing…
        </div>
      ) : null}
    </div>
  );
});
